/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { SCIMGroupCreateRequest } from '../models/SCIMGroupCreateRequest';
import type { SCIMGroupResponse } from '../models/SCIMGroupResponse';
import type { SCIMGroupUpdateRequest } from '../models/SCIMGroupUpdateRequest';
import type { SCIMListResponse } from '../models/SCIMListResponse';
import type { SCIMToken } from '../models/SCIMToken';
import type { SCIMTokenCreateRequest } from '../models/SCIMTokenCreateRequest';
import type { SCIMUserCreateRequest } from '../models/SCIMUserCreateRequest';
import type { SCIMUserResponse } from '../models/SCIMUserResponse';
import type { SCIMUserUpdateRequest } from '../models/SCIMUserUpdateRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ScimService {
  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Get Scim Tokens
   * SCIM 토큰 목록 조회.
   * @param slug
   * @returns SCIMToken Successful Response
   * @throws ApiError
   */
  public getScimTokensV1WorkspacesSlugScimV2TokensGet(
    slug: string
  ): CancelablePromise<Array<SCIMToken>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/scim/v2/tokens',
      path: {
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Scim Token
   * SCIM 토큰 생성.
   * @param slug
   * @param requestBody
   * @returns SCIMToken Successful Response
   * @throws ApiError
   */
  public createScimTokenV1WorkspacesSlugScimV2TokensPost(
    slug: string,
    requestBody: SCIMTokenCreateRequest
  ): CancelablePromise<SCIMToken> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/scim/v2/tokens',
      path: {
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Scim Token
   * SCIM 토큰 삭제.
   * @param tokenId
   * @param slug
   * @returns SCIMToken Successful Response
   * @throws ApiError
   */
  public deleteScimTokenV1WorkspacesSlugScimV2TokensTokenIdDelete(
    tokenId: number,
    slug: string
  ): CancelablePromise<SCIMToken> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/scim/v2/tokens/{token_id}',
      path: {
        token_id: tokenId,
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Users
   * SCIM v2 사용자 목록 조회.
   * @param slug
   * @param startIndex
   * @param count
   * @param filter
   * @returns SCIMListResponse Successful Response
   * @throws ApiError
   */
  public getUsersV1WorkspacesSlugScimV2UsersGet(
    slug: string,
    startIndex: number = 1,
    count: number = 100,
    filter?: string | null
  ): CancelablePromise<SCIMListResponse> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/scim/v2/Users',
      path: {
        slug: slug,
      },
      query: {
        startIndex: startIndex,
        count: count,
        filter: filter,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create User
   * SCIM v2 사용자 생성.
   * @param slug
   * @param requestBody
   * @returns SCIMUserResponse Successful Response
   * @throws ApiError
   */
  public createUserV1WorkspacesSlugScimV2UsersPost(
    slug: string,
    requestBody: SCIMUserCreateRequest
  ): CancelablePromise<SCIMUserResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/scim/v2/Users',
      path: {
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get User
   * SCIM v2 사용자 단일 조회.
   * @param userId
   * @param slug
   * @returns SCIMUserResponse Successful Response
   * @throws ApiError
   */
  public getUserV1WorkspacesSlugScimV2UsersUserIdGet(
    userId: string,
    slug: string
  ): CancelablePromise<SCIMUserResponse> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/scim/v2/Users/{user_id}',
      path: {
        user_id: userId,
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update User
   * SCIM v2 사용자 업데이트.
   * @param userId
   * @param slug
   * @param requestBody
   * @returns SCIMUserResponse Successful Response
   * @throws ApiError
   */
  public updateUserV1WorkspacesSlugScimV2UsersUserIdPatch(
    userId: string,
    slug: string,
    requestBody: SCIMUserUpdateRequest
  ): CancelablePromise<SCIMUserResponse> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspaces/{slug}/scim/v2/Users/{user_id}',
      path: {
        user_id: userId,
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update User
   * SCIM v2 사용자 업데이트.
   * @param userId
   * @param slug
   * @param requestBody
   * @returns SCIMUserResponse Successful Response
   * @throws ApiError
   */
  public updateUserV1WorkspacesSlugScimV2UsersUserIdPut(
    userId: string,
    slug: string,
    requestBody: SCIMUserUpdateRequest
  ): CancelablePromise<SCIMUserResponse> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/v1/workspaces/{slug}/scim/v2/Users/{user_id}',
      path: {
        user_id: userId,
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete User
   * SCIM v2 사용자 삭제.
   * @param userId
   * @param slug
   * @returns any Successful Response
   * @throws ApiError
   */
  public deleteUserV1WorkspacesSlugScimV2UsersUserIdDelete(
    userId: string,
    slug: string
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/scim/v2/Users/{user_id}',
      path: {
        user_id: userId,
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Groups
   * SCIM v2 그룹 목록 조회.
   * @param slug
   * @param startIndex
   * @param count
   * @param filter
   * @returns SCIMListResponse Successful Response
   * @throws ApiError
   */
  public getGroupsV1WorkspacesSlugScimV2GroupsGet(
    slug: string,
    startIndex: number = 1,
    count: number = 100,
    filter?: string | null
  ): CancelablePromise<SCIMListResponse> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/scim/v2/Groups',
      path: {
        slug: slug,
      },
      query: {
        startIndex: startIndex,
        count: count,
        filter: filter,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Group
   * SCIM v2 그룹 생성.
   * @param slug
   * @param requestBody
   * @returns SCIMGroupResponse Successful Response
   * @throws ApiError
   */
  public createGroupV1WorkspacesSlugScimV2GroupsPost(
    slug: string,
    requestBody: SCIMGroupCreateRequest
  ): CancelablePromise<SCIMGroupResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/scim/v2/Groups',
      path: {
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Group
   * SCIM v2 그룹 단일 조회.
   * @param groupId
   * @param slug
   * @returns SCIMGroupResponse Successful Response
   * @throws ApiError
   */
  public getGroupV1WorkspacesSlugScimV2GroupsGroupIdGet(
    groupId: string,
    slug: string
  ): CancelablePromise<SCIMGroupResponse> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/scim/v2/Groups/{group_id}',
      path: {
        group_id: groupId,
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Group
   * SCIM v2 그룹 업데이트.
   * @param groupId
   * @param slug
   * @param requestBody
   * @returns SCIMGroupResponse Successful Response
   * @throws ApiError
   */
  public updateGroupV1WorkspacesSlugScimV2GroupsGroupIdPatch(
    groupId: string,
    slug: string,
    requestBody: SCIMGroupUpdateRequest
  ): CancelablePromise<SCIMGroupResponse> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/workspaces/{slug}/scim/v2/Groups/{group_id}',
      path: {
        group_id: groupId,
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Group
   * SCIM v2 그룹 업데이트.
   * @param groupId
   * @param slug
   * @param requestBody
   * @returns SCIMGroupResponse Successful Response
   * @throws ApiError
   */
  public updateGroupV1WorkspacesSlugScimV2GroupsGroupIdPut(
    groupId: string,
    slug: string,
    requestBody: SCIMGroupUpdateRequest
  ): CancelablePromise<SCIMGroupResponse> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/v1/workspaces/{slug}/scim/v2/Groups/{group_id}',
      path: {
        group_id: groupId,
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Group
   * SCIM v2 그룹 삭제.
   * @param groupId
   * @param slug
   * @returns any Successful Response
   * @throws ApiError
   */
  public deleteGroupV1WorkspacesSlugScimV2GroupsGroupIdDelete(
    groupId: string,
    slug: string
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/scim/v2/Groups/{group_id}',
      path: {
        group_id: groupId,
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }
}
